import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BadgeCheck, KeyRound, Loader2, LineChart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermission } from '@/features/auth/auth.store';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ManagerCodeDialog } from './manager-code-dialog';
import { PerformanceSheet } from './performance-sheet';
import {
  READ_ONLY_PRIVILEGES,
  STAFF_NOTIFICATIONS,
  STAFF_PRIVILEGES,
  toProfileInput,
  useManagerCode,
  useSendLoginPin,
  useUpdateStaffProfile,
  type StaffProfile,
} from './staff.api';

/**
 * One person's access to this store.
 *
 * The original put all of this in a row that expanded in place, with every
 * switch saving on its own — nineteen round trips to change nineteen things,
 * and no way to see what you had changed. Here it is a drawer with one Save.
 *
 * Whether the switches can be touched at all is the store's rule, not a
 * cosmetic one: an admin, or somebody the store has given `canEditUsers`.
 */
export function StaffDetailSheet({
  profile,
  canEdit,
  open,
  onOpenChange,
}: {
  profile: StaffProfile | null;
  canEdit: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const isAdmin = usePermission('isAdmin');
  const isSuperAdmin = usePermission('isSuperAdmin');

  const update = useUpdateStaffProfile();
  const managerCode = useManagerCode();
  const sendPin = useSendLoginPin();

  const [draft, setDraft] = useState<StaffProfile | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);
  const [lanes, setLanes] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [showPerformance, setShowPerformance] = useState(false);

  if (profile && hydrated !== profile.id) {
    setHydrated(profile.id);
    setDraft(profile);
    setLanes(false);
  }

  if (!draft || !profile) return null;

  const set = (patch: Partial<StaffProfile>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const failed = () => toast.error(t('staff.actionFailed'));

  function save() {
    update.mutate(
      {
        profileId: draft!.id,
        input: toProfileInput(draft!, lanes),
      },
      {
        onSuccess: () => {
          toast.success(t('staff.saved'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  const name = draft.completeName ?? draft.email ?? '';

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle className="truncate">{name}</SheetTitle>
            <p className="text-muted-foreground truncate text-sm">
              {draft.email}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={draft.isActive ? 'success' : 'secondary'}>
                {draft.isActive ? t('staff.active') : t('staff.inactive')}
              </Badge>
              <Badge variant={draft.isAdmin ? 'default' : 'secondary'}>
                {draft.isAdmin ? t('staff.admin') : t('staff.regular')}
              </Badge>
              {draft.createdAt && (
                <span className="text-muted-foreground text-xs">
                  {t('staff.since')} {formatDateTime(draft.createdAt)}
                </span>
              )}
            </div>
          </SheetHeader>

          <div className="space-y-3 p-4">
            <div className="flex flex-wrap gap-2">
              {isAdmin && draft.userId && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={managerCode.isPending}
                  onClick={() =>
                    managerCode.mutate(draft.userId!, {
                      onSuccess: setCode,
                      onError: failed,
                    })
                  }
                >
                  {managerCode.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <BadgeCheck className="size-3.5" />
                  )}
                  {t('staff.managerCode')}
                </Button>
              )}
              {draft.userId && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sendPin.isPending}
                  onClick={() =>
                    sendPin.mutate(draft.userId!, {
                      onSuccess: (envelope) => {
                        // The PIN comes back in `message`, not `data`, and the
                        // original copied it as well as showing it.
                        const message = envelope.message;
                        if (message) {
                          void navigator.clipboard.writeText(message);
                          toast.success(t('staff.pinCopied', { pin: message }));
                        } else {
                          toast.success(t('staff.pinSent'));
                        }
                      },
                      onError: failed,
                    })
                  }
                >
                  {sendPin.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <KeyRound className="size-3.5" />
                  )}
                  {t('staff.sendPin')}
                  {isSuperAdmin && draft.userLoginPin && (
                    <span className="font-mono">{draft.userLoginPin}</span>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPerformance(true)}
              >
                <LineChart className="size-3.5" />
                {t('staff.performance')}
              </Button>
            </div>

            <Tabs defaultValue="privileges">
              <TabsList className="w-full">
                <TabsTrigger value="privileges" className="flex-1">
                  {t('staff.privileges')}
                </TabsTrigger>
                <TabsTrigger value="notifications" className="flex-1">
                  {t('staff.notifications')}
                </TabsTrigger>
                <TabsTrigger value="roles" className="flex-1">
                  {t('staff.roles')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="privileges" className="space-y-2">
                <Toggle
                  label={t('staff.isActive')}
                  hint={t('staff.isActiveHint')}
                  checked={draft.isActive}
                  disabled={!canEdit}
                  onChange={(isActive) => set({ isActive })}
                />
                {isAdmin && (
                  <>
                    <Toggle
                      label={t('staff.admin')}
                      checked={draft.isAdmin}
                      disabled={!canEdit}
                      onChange={(isAdminFlag) => set({ isAdmin: isAdminFlag })}
                    />
                    <Toggle
                      label={t('staff.hiddenFromStaff')}
                      checked={draft.hiddenFromStaff}
                      disabled={!canEdit}
                      onChange={(hiddenFromStaff) => set({ hiddenFromStaff })}
                    />
                  </>
                )}

                {STAFF_PRIVILEGES.map((key) => (
                  <Toggle
                    key={key}
                    label={t(`staff.${key}`)}
                    checked={draft[key]}
                    disabled={!canEdit}
                    onChange={(value) => set({ [key]: value })}
                  />
                ))}

                {/* Reported by the row but absent from the update model, so
                    they are shown as they stand and cannot be changed here. */}
                <p className="pt-2 text-sm font-medium">
                  {t('staff.notEditableHere')}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t('staff.notEditableHint')}
                </p>
                <div className="divide-y rounded-xl border">
                  {READ_ONLY_PRIVILEGES.map((key) => (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <span className="text-sm">{t(`staff.${key}`)}</span>
                      <Badge variant={draft[key] ? 'success' : 'secondary'}>
                        {draft[key] ? t('staff.yes') : t('staff.no')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="notifications" className="space-y-2">
                {STAFF_NOTIFICATIONS.map((key) => (
                  <Toggle
                    key={key}
                    label={t(`staff.${key}`)}
                    checked={draft[key]}
                    disabled={!canEdit}
                    onChange={(value) => set({ [key]: value })}
                  />
                ))}
                {/* The row never reports this one, so it starts off and has to
                    be set deliberately — see staff.api.ts. */}
                <Toggle
                  label={t('staff.getLaneNotifications')}
                  hint={t('staff.getLaneNotificationsHint')}
                  checked={lanes}
                  disabled={!canEdit}
                  onChange={setLanes}
                />
              </TabsContent>

              <TabsContent value="roles">
                {(draft.roles ?? []).length === 0 ? (
                  <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
                    {t('staff.noRoles')}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {(draft.roles ?? []).map((role) => (
                      <Badge key={role} variant="secondary">
                        {role}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-muted-foreground mt-2 text-xs">
                  {t('staff.rolesHint')}
                </p>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                disabled={update.isPending}
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button disabled={!canEdit || update.isPending} onClick={save}>
                {update.isPending && <Loader2 className="animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {code !== null && draft.userId && (
        <ManagerCodeDialog
          name={name}
          userId={draft.userId}
          code={code}
          canRegenerate={isAdmin}
          open
          onOpenChange={(next) => !next && setCode(null)}
          onRegenerated={setCode}
        />
      )}

      <PerformanceSheet
        profileId={showPerformance ? draft.id : null}
        name={name}
        open={showPerformance}
        onOpenChange={setShowPerformance}
      />
    </>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border px-3 py-2',
        disabled && 'opacity-60'
      )}
    >
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {hint && (
          <span className="text-muted-foreground block text-xs">{hint}</span>
        )}
      </span>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </label>
  );
}
